import { theme } from 'coreOld/themes';

const getBorderStyle = (theme, hasBorder = true, hasError = false) => {
  if (hasError) {
    return {
      border: `1px solid ${theme.colors.dangerBtn}`,
    };
  }

  if (!hasBorder) {
    return {
      border: `1px solid ${theme.palette.neutral.light}`,
    };
  }

  return {
    border: `1px solid ${theme.palette.borderColor}`,
  };
};

const getHoverBorderStyle = (theme, hasBorder = true, hasError = false) => {
  if (hasError) {
    return {
      border: `1px solid ${theme.colors.dangerBtn}`,
    };
  }

  if (!hasBorder) {
    return {
      border: `1px solid ${theme.palette.hover.main}`,
    };
  }

  return {
    border: `1px solid ${theme.palette.hover.borderColor}`,
  };
};

const getFocusedBorderStyle = (theme, hasBorder = true, hasError = false, isFocused = false) => {
  if (!isFocused) {
    return {};
  }

  return getHoverBorderStyle(theme, hasBorder, hasError);
};

const getDisabledStyle = (isDisabled = false) => {
  if (!isDisabled) {
    return {};
  }

  return {
    fontSize: 'inherit',
    fontWeight: 400,
    border: 'none',
    backgroundColor: 'inherit',
    textAlign: 'right',
    color: theme.palette.neutral.darkest,
  };
};

const getFontStyle = (fontInherit: boolean, fontSize?: number | string) => {
  if (fontInherit) {
    return {
      fontSize: 'inherit',
    };
  }

  return {
    fontSize: fontSize || 12,
  };
};

export const createCustomStyles = (
  theme,
  size?: string,
  fontInherit?: boolean,
  hasBorder?: boolean,
  hasError = false
) => ({
  menu: (provided) => ({
    ...provided,
    marginTop: 0,
    boxShadow: 'none',
    borderTop: 'none',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    maxHeight: 142,
    ...getBorderStyle(theme, hasBorder),
  }),
  container: (provided) => ({
    ...provided,
    height: 'fit-content',
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 142,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: 'none' }),
  control: (provided, { isDisabled, isFocused }) => ({
    ...provided,
    borderRadius: 0,
    minHeight: theme.inputHeight[size] || 45,
    boxShadow: 'none',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    ...getBorderStyle(theme, hasBorder, hasError),
    ...getDisabledStyle(isDisabled),
    opacity: isDisabled ? 0.5 : 1,
    ...getFocusedBorderStyle(theme, hasBorder, hasError, isFocused),

    '&:hover,&:focus': {
      boxShadow: 'none',
      ...getHoverBorderStyle(theme, hasBorder, hasError),
    },
  }),
  placeholder: (provided, { isDisabled }) => ({
    ...provided,
    color: theme.palette.text.placeholder,
    fontWeight: 400,
    ...getFontStyle(fontInherit),
    ...getDisabledStyle(isDisabled),
  }),
  option: (provided, state) => {
    return {
      ...provided,
      color: state.isSelected
        ? theme.fonts.dropdownHover?.color || theme.palette.text.secondary
        : theme.palette.text.dropdown,
      backgroundColor: state.isSelected ? theme.colors.dropdownHover : 'transparent',
      textDecoration: state.isSelected && theme.fonts.dropdownHover?.textDecoration,
      fontStyle: state.isSelected && theme.fonts.dropdownHover?.fontStyle,
      fontWeight: (state.isSelected && theme.fonts.dropdownHover?.fontWeight) || 400,
      ...getFontStyle(fontInherit, state.isSelected && theme.fonts.dropdownHover?.fontSize),

      '&:hover,&:focus': {
        backgroundColor: theme.colors.dropdownHover,
        textDecoration: theme.fonts.dropdownHover?.textDecoration,
        fontWeight: theme.fonts.dropdownHover?.fontWeight,
        fontStyle: theme.fonts.dropdownHover?.fontStyle,
        color: theme.fonts.dropdownHover?.color || theme.palette.text.secondary,
        cursor: 'pointer',
        ...getFontStyle(fontInherit, theme.fonts.dropdownHover?.fontSize),
      },
    };
  },
  singleValue: (provided, { isDisabled }) => ({
    ...provided,
    fontWeight: 400,
    ...getDisabledStyle(isDisabled),
    ...getFontStyle(fontInherit),
  }),
  multiValueLabel: ({ isDisabled }) => ({
    fontSize: '13px',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginRight: 5,
    ...getDisabledStyle(isDisabled),
  }),
  multiValueRemove: () => ({
    color: theme.palette.text.secondary,
    fontSize: '9px',
    border: `1px solid ${theme.palette.backgrounds.main}`,
    borderRadius: '50%',

    '&:hover': {
      cursor: 'pointer',
    },
  }),
  multiValue: (provided, { isDisabled }) => ({
    ...provided,
    backgroundColor: theme.palette.primary.dark,
    border: `1px solid ${theme.palette.primary.dark}`,
    padding: '6px 10px 6px 10px',
    margin: '0 4.5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: isDisabled ? 'right' : 'inherit',
  }),
});
